/**
 * Tema claro/oscuro — Fase 0 (sep-2026).
 * · Default OSCURO. La clase `light` en <html> activa los tokens claros (index.css); el dorado no cambia.
 * · Preferencia por usuario en backend (users.ui_theme) + espejo en localStorage (evita destello al abrir).
 * · Aplicación instantánea: toggle de clase ⇒ todas las variables CSS cambian en el mismo frame, sin recarga.
 * · `canSwitch` controla la visibilidad del switch (founder / QA / flag global de la Fase 5).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export const THEME_STORAGE_KEY = 'lineage_theme';
const THEME_COLORS = { dark: '#000000', light: '#F9F7F2' };
const ThemeContext = createContext({ theme: 'dark', canSwitch: false, setTheme: () => {} });

export const applyThemeToDocument = (theme) => {
  const light = theme === 'light';
  document.documentElement.classList.toggle('light', light);
  document.documentElement.style.colorScheme = light ? 'light' : 'dark';
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLORS[light ? 'light' : 'dark']);
};

const readLocal = () => {
  try { return localStorage.getItem(THEME_STORAGE_KEY) === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
};

export const ThemeProvider = ({ children }) => {
  const { user, sessionVersion } = useAuth();
  const userId = user?.id || null;
  const [theme, setThemeState] = useState(readLocal);
  const [canSwitch, setCanSwitch] = useState(false);

  useEffect(() => { applyThemeToDocument(theme); }, [theme]);

  useEffect(() => {
    if (!userId) {
      setCanSwitch(false);
      setThemeState('dark');
      try { localStorage.removeItem(THEME_STORAGE_KEY); } catch { /* noop */ }
      return;
    }
    let alive = true;
    api.get('/users/me/appearance').then((r) => {
      if (!alive) return;
      const t = r?.theme === 'light' ? 'light' : 'dark';
      setCanSwitch(!!r?.can_switch);
      setThemeState(t);
      try { localStorage.setItem(THEME_STORAGE_KEY, t); } catch { /* noop */ }
    }).catch(() => { if (alive) setCanSwitch(false); });
    return () => { alive = false; };
  }, [userId, sessionVersion]); // id estable + señal de login: re-consulta tras re-login de la MISMA cuenta, no en cada refresh del objeto user

  const setTheme = useCallback(async (next) => {
    const t = next === 'light' ? 'light' : 'dark';
    setThemeState(t);
    try { localStorage.setItem(THEME_STORAGE_KEY, t); } catch { /* noop */ }
    if (userId) await api.put('/users/me/appearance', { theme: t });
  }, [userId]);

  const value = useMemo(() => ({ theme, canSwitch, setTheme }), [theme, canSwitch, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
